import { FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { useApp } from "~/core";
import { useTimeout } from "~/hooks";
import Popup from "./Popup";
import { registerMarquee, setInitialTouch } from "./dispatch";
import { Coords, MarqueeProps } from "./interfaces";
import cn from "classnames";

const MS_PER_PIXEL = 14;
const ANIMATION_DELAY_MS = 1200;
const LONG_PRESS_MS = 500;
const POPUP_X_MARGIN = 12;
const POPUP_Y_OFFSET = 8;
const POPUP_MIN_MARGIN = 72;
const POPUP_MAX_HEIGHT = 120;
const PADDING = 60;

const timers = new WeakMap<Element, number>();

const observer = new IntersectionObserver(
    (entries) => {
        entries.forEach((entry) => {
            const target = entry.target;
            const track = target.querySelector<HTMLElement>(
                "[data-marquee-track]",
            );

            if (!track) {
                return;
            }

            if (entry.isIntersecting) {
                const delay = track.dataset.delay
                    ? parseInt(track.dataset.delay, 10)
                    : NaN;
                const tmId = window.setTimeout(
                    () => {
                        track.style.animationName = "marquee";
                    },
                    isNaN(delay) ? ANIMATION_DELAY_MS : delay,
                );

                timers.set(target, tmId);
            } else {
                track.style.animationName = "none";
                window.clearTimeout(timers.get(target));
            }
        });
    },
    { threshold: 1 },
);

const Marquee: FC<MarqueeProps> = ({ children, text, delay, className }) => {
    const { mobile, settings } = useApp();
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const preventClick = useRef(false);
    const hasOverflow = useSignal(false);
    const isPopup = useSignal(false);
    const popupCoords = useSignal<Coords>({});
    const longPress = useTimeout(LONG_PRESS_MS);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => registerMarquee(isPopup, () => longPress(false)), []);

    useEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;
        const animate = settings.marqueeEnabled.value;

        if (!container || !content) {
            return;
        }

        const contentWidth = content.offsetWidth;
        const containerWidth = container.offsetWidth;

        if (contentWidth - PADDING <= containerWidth) {
            hasOverflow.value = false;
            return;
        }

        hasOverflow.value = true;

        window.dispatchEvent(new CustomEvent("marquee.register"));

        if (!animate) {
            return;
        }

        const track = container.querySelector<HTMLElement>(
            "[data-marquee-track]",
        );
        const duration = Math.round(contentWidth * MS_PER_PIXEL);
        const width = Math.round(contentWidth);

        container.style.setProperty("--marquee-duration", `${duration}ms`);
        container.style.setProperty("--marquee-width", `${width}px`);
        observer.observe(container);

        return () => {
            window.clearTimeout(timers.get(container));
            observer.unobserve(container);
            container.style.removeProperty("--marquee-width");
            container.style.removeProperty("--marquee-duration");
            hasOverflow.value = false;

            if (track) {
                track.style.animationName = "none";
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, settings.marqueeEnabled.value]);

    return (
        <>
            <div
                ref={containerRef}
                className={cn(
                    "overflow-hidden whitespace-nowrap w-full",
                    mobile.isIOS && "ios-stacking-context",
                    className,
                )}
                onTouchStart={(e) => {
                    if (!hasOverflow.peek() || e.touches.length !== 1) {
                        return;
                    }

                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = rect.top - POPUP_Y_OFFSET - POPUP_MAX_HEIGHT;
                    const coords: Coords = { left: `${POPUP_X_MARGIN}px` };

                    if (y < POPUP_MIN_MARGIN) {
                        coords.top = `${rect.bottom + POPUP_Y_OFFSET}px`;
                    } else {
                        coords.bottom = `${window.innerHeight - rect.top + POPUP_Y_OFFSET}px`;
                    }

                    setInitialTouch(e.touches[0].clientX, e.touches[0].clientY);

                    longPress(() => {
                        popupCoords.value = coords;
                        isPopup.value = true;
                        mobile.impact("medium");
                    });
                }}
                onTouchEnd={(e) => {
                    if (isPopup.peek()) {
                        e.preventDefault();
                        e.stopPropagation();
                        longPress(false);
                        isPopup.value = false;

                        if (mobile.isIOS) {
                            preventClick.current = true;
                        }
                    }
                }}
                onClick={(e) => {
                    if (preventClick.current) {
                        e.preventDefault();
                        e.stopPropagation();
                        preventClick.current = false;
                    }
                }}
            >
                <div
                    data-marquee-track
                    data-delay={delay}
                    className="flex flex-shrink-0 hardware-accelerated will-change-transform"
                    style={{
                        animationDuration: "var(--marquee-duration, 10s)",
                        animationTimingFunction: "linear",
                        animationIterationCount: "infinite",
                        animationPlayState: isPopup.value
                            ? "paused"
                            : "running",
                    }}
                >
                    <div ref={contentRef} className="pr-16">
                        {children}
                    </div>
                    {hasOverflow.value && (
                        <div className="pr-16">{children}</div>
                    )}
                </div>
            </div>
            <Popup show={isPopup} coords={popupCoords}>
                {children}
            </Popup>
        </>
    );
};

export default Marquee;
